import React, { useState, useEffect } from 'react';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Globe, Loader2, Check } from 'lucide-react';
import {
  ClientFilters, useClientSearch, Pager, EmptyMsg, apiError,
} from './salesShared';

// Research queue: clients with no phone and no email. Fill in a number or
// an address and the client leaves this list and can go on a call list.
const NeedsDetailsTab = ({ salesReps, facets, defaultRep, onChanged }) => {
  const { user } = useAuth();
  const isSalesRep = user?.role === 'sales_rep';
  const [filters, setFilters] = useState({ q: '', rep: defaultRep, industry: 'all', needs_details: true });
  const search = useClientSearch(filters, { pageSize: 25, sort: 'name' });
  const [drafts, setDrafts] = useState({});      // client id -> { phone, email, contact_name }
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { setFilters(f => ({ ...f, rep: defaultRep })); }, [defaultRep]);

  const draft = (id) => drafts[id] || { phone: '', email: '', contact_name: '' };
  const edit = (id, patch) => setDrafts(d => ({ ...d, [id]: { ...draft(id), ...patch } }));

  const save = async (c) => {
    const d = draft(c.id);
    const payload = {};
    if (d.phone.trim()) payload.phone = d.phone.trim();
    if (d.email.trim()) payload.email = d.email.trim().toLowerCase();
    if (d.contact_name.trim()) payload.contact_name = d.contact_name.trim();
    if (!payload.phone && !payload.email) {
      setError('Enter a phone number or an email address.');
      return;
    }
    setSaving(c.id);
    setError('');
    try {
      await api.put(`/advertisers/${c.id}`, payload);
      setDrafts(({ [c.id]: _done, ...rest }) => rest);
      await search.reload();
      if (onChanged) onChanged();
    } catch (e) {
      setError(apiError(e, 'Could not save'));
    } finally {
      setSaving(null);
    }
  };

  const inputCls = 'bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-8 text-sm';

  return (
    <div className="space-y-3" data-testid="needs-details-tab">
      <ClientFilters
        filters={filters} onChange={setFilters} facets={facets} salesReps={salesReps} isSalesRep={isSalesRep}
        show={{ industry: true, rep: true, status: false, hasPhone: false, postal: false }}
        placeholder="Search by name or address…"
      />
      <p className="text-sm text-[#94A3B8]" data-testid="needs-details-count">
        {search.loading ? 'Loading…' : `${search.total.toLocaleString()} client(s) have no phone and no email.`}
        {' '}Add either one and the client moves on to the call lists.
      </p>
      {(error || search.error) && <div className="text-sm text-red-400" data-testid="needs-details-error">{error || search.error}</div>}

      <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="needs-details-table">
          <thead className="bg-[#0A0A0A] text-[#94A3B8] text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-left px-3 py-2">Phone</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Contact person</th>
              <th className="text-right px-3 py-2">Save</th>
            </tr>
          </thead>
          <tbody>
            {search.items.map(c => {
              const d = draft(c.id);
              const lookup = `https://www.google.com/search?q=${encodeURIComponent(`${c.business_name} ${c.address || 'Red Deer AB'}`)}`;
              return (
                <tr key={c.id} className="border-t border-[#1E293B]" data-testid={`needs-row-${c.id}`}>
                  <td className="px-3 py-2">
                    <div className="text-[#F8FAFC]">{c.business_name}</div>
                    <div className="text-xs text-[#475569]">
                      {[c.industry, c.address, c.location_type].filter(Boolean).join(' · ')}
                      {' '}
                      <a href={lookup} target="_blank" rel="noreferrer" className="text-[#3B82F6] inline-flex items-center gap-1" data-testid={`needs-lookup-${c.id}`}>
                        <Globe className="w-3 h-3" /> look up
                      </a>
                    </div>
                  </td>
                  <td className="px-3 py-2"><Input value={d.phone} onChange={e => edit(c.id, { phone: e.target.value })} placeholder="(403) 555-1234" className={`${inputCls} w-36`} data-testid={`needs-phone-${c.id}`} /></td>
                  <td className="px-3 py-2"><Input value={d.email} onChange={e => edit(c.id, { email: e.target.value })} placeholder="name@business.ca" className={`${inputCls} w-52`} data-testid={`needs-email-${c.id}`} /></td>
                  <td className="px-3 py-2"><Input value={d.contact_name} onChange={e => edit(c.id, { contact_name: e.target.value })} className={`${inputCls} w-40`} data-testid={`needs-contact-${c.id}`} /></td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" onClick={() => save(c)} disabled={saving === c.id || (!d.phone.trim() && !d.email.trim())} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-8" data-testid={`needs-save-${c.id}`}>
                      {saving === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!search.loading && search.items.length === 0 && <EmptyMsg text="Every client on this list has a phone or an email." />}
      </div>
      <Pager page={search.page} pages={search.pages} total={search.total} pageSize={search.pageSize} onPage={search.setPage} testid="needs-details-pager" />
    </div>
  );
};

export default NeedsDetailsTab;
